// src/utils/createUserRecord.js
import { doc, setDoc, serverTimestamp, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../Security/firebase-config";

const DEFAULT_MAX_TEAMS = 2;

export async function createUserRecord(
  user,
  {
    username = undefined, // IMPORTANT: undefined = "don't touch", null = "explicitly set null"
    theme = "dark",
    teamId = null,

    // optional overrides:
    needsEmailVerification = undefined,
    emailVerified = undefined,
    verifiedUnlockedAt = undefined,
  } = {}
) {
  if (!user?.uid) return;

  const userRef = doc(db, "users", user.uid);
  const snap = await getDoc(userRef);

  const providers = Array.isArray(user?.providerData)
    ? user.providerData.map((p) => p.providerId)
    : [];

  const isPassword = providers.includes("password");

  const computedEmailVerified =
    typeof emailVerified !== "undefined" ? emailVerified : !!user?.emailVerified;

  const computedNeedsEmailVerification =
    typeof needsEmailVerification !== "undefined"
      ? needsEmailVerification
      : isPassword
      ? true
      : false;

  // ─────────────────────────────────────────────────────────────
  // 1) CREATE (doc does not exist) → allowed to write full shape
  // ─────────────────────────────────────────────────────────────
  if (!snap.exists()) {
    // Your /users create rule requires:
    // - hasAll(username, email, createdAt)
    // - email must be string
    // - createdAt timestamp
    const createPayload = {
      // Identity
      uid: user.uid,
      email: user.email ?? "", // rule expects string
      username: typeof username === "undefined" ? null : username,

      // Auth metadata
      providers,
      emailVerified: computedEmailVerified,
      needsEmailVerification: computedNeedsEmailVerification,

      verifiedUnlockedAt: verifiedUnlockedAt ?? null,

      // App defaults
      maxTeams: DEFAULT_MAX_TEAMS,
      teamId: teamId ?? null,
      theme,

      createdAt: serverTimestamp(),
    };

    await setDoc(userRef, createPayload, { merge: false });
    return;
  }

  // ─────────────────────────────────────────────────────────────
  // 2) UPDATE (doc exists) → ONLY write fields allowed by your update rule
  // Your /users update rule allows: username, email, theme, etc.
  // It does NOT allow: providers, emailVerified, needsEmailVerification,
  // createdAt, teamId...
  // ─────────────────────────────────────────────────────────────
  const patch = {};

  // Only set username if it's a string (avoid wiping with null)
  if (typeof username === "string" && username.trim()) {
    patch.username = username.trim();
  }

  // Email is allowed to update (per your rule list)
  if (typeof user.email === "string" && user.email) {
    patch.email = user.email;
  }

  if (typeof theme === "string" && theme) {
    patch.theme = theme;
  }

  // Nothing to update? Stop.
  if (!Object.keys(patch).length) return;

  await updateDoc(userRef, patch);
}
